'use client';

import { sessionQueryOptions } from '@/lib/session';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * The setup page's account step: makes their Borradh user and organization
 * and signs them in (the API sets the session cookie), or ties the account
 * they are already signed in to to their deal.
 */
export const useCreateClinicAccount = (
  token: string,
  options?: { onSuccess?: () => void }
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      email?: string;
      businessName?: string;
      /** Use the account this browser is signed in to. */
      useCurrentAccount?: boolean;
    }) =>
      apiClient.post<{ organizationId: string }>(
        `crm/clinic/${encodeURIComponent(token)}/account`,
        input
      ),
    onSuccess: () => {
      // Signed in now: every query that depends on the session reloads.
      void queryClient.invalidateQueries();
      options?.onSuccess?.();
    },
    // A failure after their login was made still signs them in: the session
    // reloads and the step offers to carry on from that account.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: sessionQueryOptions.queryKey,
      });
    },
  });
  return {
    createAccount: mutation.mutate,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
};
